import { AppSettingContext, RegiftCardContext } from "@src/components/Application";
import { IBuyGiftPaymentStepProps, IRegiftPaymentStepProps } from "@src/models/props/PaymentStepProps.interface";
import React, { useEffect, useRef, useState } from "react";
import { PAGE_LINKS } from "../../constants/global";
import { findLinkByAlias } from "../../helpers/GiftCardHelper";
import { BuyGiftCardModelOld } from "../../models/BuyGiftCard.model";
import { BuyGiftCardStateEnum } from "../../models/enums/BuyGiftCardStateEnum";
import { PaymentTypeEnum } from "../../models/enums/PaymentTypeEnum";
import { IPaymentStep, PaymentStepModel } from "../../models/PaymentStep.model";
import { BuyGiftCardContext } from "../../pages/BuyGiftCardPage";
import DataService from "../../services/DataService.service";
import LoadingComponent from "../shared/LoadingComponent";
import SelectPaymentMethod from "./SelectPaymentMethod";
import BasketDetails from "./shared/BasketDetails";
import * as GlobalConstants from "@src/constants/global";
import StripePayment from "./shared/StripePayment";
import { CheckBalanceStepModel } from "../../models/CheckBalanceStep.model";
import { RegiftCardStepModel } from "../../models/RegiftCardStep.model";
import { RecipientsStepModel } from "../../models/RecipientsStep.model";
import { CustomizeStepModel } from "../../models/CustomizeStep.model";
import { IRegiftPaymentStep, RegiftPaymentStepModel } from "../../models/RegiftPaymentStep.model";
import { RegiftCardStateEnum } from "../../models/enums/RegiftCardStateEnum";
import { WorkflowTypeEnum } from "../../models/enums/WorkflowTypeEnum";


const RegiftCardPaymentStep = (props: IRegiftPaymentStepProps) => {

    const { id, currentState, checkBalanceStep, regiftCardStep, recipientsStep, customizeStep, paymentStep } = React.useContext(RegiftCardContext);
    const { debugMode, links, labels } = React.useContext(AppSettingContext);

    const IS_ACTIVE = (checkBalanceStep && CheckBalanceStepModel.validateModel(checkBalanceStep) && !checkBalanceStep.isDirty)
        && (regiftCardStep && RegiftCardStepModel.validateModel(regiftCardStep, checkBalanceStep.remainingBalance) && !regiftCardStep.isDirty)
        && (recipientsStep && RecipientsStepModel.validateModel(recipientsStep, WorkflowTypeEnum.REGIFT_CARD) && !recipientsStep.isDirty)
        && (customizeStep && CustomizeStepModel.validateModel(customizeStep) && !customizeStep.isDirty);

    const [pageModel, setPageModel] = useState<IRegiftPaymentStep>(undefined);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkboxRef = useRef(null)
    const termsPageUrl = findLinkByAlias("terms-page", links);
    const termsLink = labels.get("GiftCard.App.Steps.PreviewAndPay.TermsAndConditions").replace("{0}", termsPageUrl.url ?? "");
    const defaultPersonalMessageText = labels.get("GiftCard.App.Steps.Customise.PersonalMessage.DefaultText");

    useEffect(() => {
        checkboxRef.current.checked = props.state == RegiftCardStateEnum.REVIEW_PAY || IS_ACTIVE;
    });

    useEffect(() => {

        //local model initialization
        setIsLoading(true);
        //console.debug("paymentStep", props.giftCard.paymentStep);

        // Page Model
        setPageModel({ ...props.giftCard.paymentStep });
    }, []);

    useEffect(() => {
        setIsLoading(true);
        if (pageModel) {
            setIsLoading(false);

            const result = checkIfModelIsDirty();
            setIsDirty(result);
            props.handleDirtyState(result);
        }
    }, [pageModel]);

    useEffect(() => {
        if (error) {
            props.handleError(error);
        }
    }, [error]);

    const isValid = () => {
        let copyPageModel = { ...pageModel };
        return RegiftPaymentStepModel.validateModel(copyPageModel);
    };

    const checkIfModelIsDirty = (): boolean => {
        if (!pageModel) return false;

        // check
        if (!pageModel.termsAccepted || pageModel.termsAccepted != props.giftCard.paymentStep.termsAccepted) {
            return true;
        }

        return false;
    }

    //this should be call always
    const handleExpandCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (checkboxRef.current.checked) {
            props.handleBeginEdit();
        }
    }

    const handleCheckout = (e: React.MouseEvent<HTMLButtonElement>) => {
        //do not propagate changes
        setIsSaving(true);

        const copyModel = Object.assign({}, props.giftCard);

        //Do not save default message
        if (copyModel.customizeStep.personalMessage === defaultPersonalMessageText) {
            copyModel.customizeStep.personalMessage = '';
        }

        let culture = document.documentElement.lang;
        //Proceed with checkout
        DataService.checkoutRegiftCard(culture, copyModel.id, copyModel)
            .then(response => {
                //console.debug(response);
                if (response && response.id && response.id.trim()) {
                    // clear local storage
                    localStorage.removeItem(GlobalConstants.LOCALSTORAGE_KEY_BASKET_ID)

                    //get confirmation page link
                    let pageLink = findLinkByAlias(PAGE_LINKS.BUY_GIFT_CARD_CONFIRMATION, links);
                    if (pageLink && pageLink.url.trim()) {
                        var queryString = {
                            bid: props.giftCard.id
                        };
                        let url = pageLink.url + "?" + new URLSearchParams(queryString).toString();

                        //redirect to confirmation page
                        window.location.replace(url);
                    }
                } else {
                    setError("Unable to checkout");
                    setIsSaving(false); //in case of an error
                }
            })
            .catch(console.error)
            .finally(() => {
                //setIsSaving(false); do not do this, wait for a redirect in case of success 
            });
    }

    //if (error) throw error;
    return (
        <div className="review-pay box">
            <input ref={checkboxRef}
                defaultChecked={false}
                onChange={handleExpandCheck}
                type="checkbox" name="review-pay-hide" id="review-pay-hide" data-review-pay-box-hide />

            <div className={`action-card ${IS_ACTIVE ? "" : "inactive"}`}
                style={{
                    pointerEvents: isSaving ? "none" : "auto",
                }}>
                <h3>{labels.get("GiftCard.App.Steps.PreviewAndPay")}</h3>
                <div className="edit">
                    {IS_ACTIVE &&
                        <label htmlFor="review-pay-hide" className="label-fs">
                            <i></i>
                        </label>}
                </div>

                <div className="show">
                    {!isLoading &&
                        <div className="slide-content" aria-disabled
                            style={{
                                opacity: isSaving ? 0.2 : 1
                            }}>
                            {(debugMode) &&
                                <div>
                                    <p>Terms Accepted: {String(pageModel.termsAccepted)}</p>

                                    <p>Is dirty: {String(isDirty)}</p>
                                    <p>Is valid: {String(isValid())}</p>
                                </div>}

                            <BasketDetails
                                giftCard={props.giftCard}
                                recipients={props.giftCard.recipientsStep.recipients} />


                            <hr />
                            <div className="flex-container form-field">
                                <input type="checkbox"
                                    name="payment-type__accept"
                                    id="payment-type__accept"
                                    defaultChecked={props.giftCard.paymentStep.termsAccepted}
                                    onChange={(e) => setPageModel({ ...pageModel, termsAccepted: e.target.checked })}
                                />
                                <label htmlFor="payment-type__accept" dangerouslySetInnerHTML={{ __html: termsLink }} />
                            </div>
                            <hr />

                            {(isSaving) ? (
                                <LoadingComponent />
                            ) : (
                                <>
                                    <button className="btn btn__primary btn__large btn__full_width large-fs"
                                        disabled={!isValid() || !isDirty}
                                        onClick={handleCheckout}>{labels.get("GiftCard.App.Steps.PreviewAndPay.Btn.Pay")} &euro;{props.giftCard.paymentStep.paymentDetails.totalAmount.toFixed(2)}
                                    </button>
                                    <button className="btn btn__outlined btn__large btn__full_width large-fs"
                                        onClick={props.handlePreview}>{labels.get("GiftCard.App.Steps.PreviewAndPay.Btn.Preview")}</button>
                                </>
                            )}

                        </div>}
                </div>
            </div>
        </div>
    )
}

export default RegiftCardPaymentStep;